import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Ticket, Plus, CheckCircle, XCircle, User, Calendar, Clock, Copy, Ban } from 'lucide-react'
import { api, type PromoCode } from '../lib/api'
import { queries } from '../lib/queries'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Card, CardContent } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Skeleton } from '../components/ui/skeleton'

export const Route = createFileRoute('/admin/promo-codes')({
  head: () => ({
    meta: [{ title: 'Promo Codes | Admin | Unbreakable' }],
  }),
  component: AdminPromoCodesPage,
})

type PromoCodeStatus = 'available' | 'used' | 'expired' | 'revoked'

function getPromoCodeStatus(code: PromoCode): PromoCodeStatus {
  if (code.isRevoked) return 'revoked'
  if (code.usedCount >= code.maxUses) return 'used'
  if (code.expiresAt && new Date(code.expiresAt) < new Date()) return 'expired'
  return 'available'
}

function StatusBadge({ status }: { status: PromoCodeStatus }) {
  const config = {
    available: { variant: 'default' as const, icon: CheckCircle, label: 'Available' },
    used: { variant: 'secondary' as const, icon: User, label: 'Used' },
    expired: { variant: 'outline' as const, icon: Clock, label: 'Expired' },
    revoked: { variant: 'destructive' as const, icon: XCircle, label: 'Revoked' },
  }
  const { variant, icon: Icon, label } = config[status]
  return <Badge variant={variant} className="gap-1"><Icon className="h-3 w-3" />{label}</Badge>
}

function AdminPromoCodesPage() {
  const queryClient = useQueryClient()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showRevokeDialog, setShowRevokeDialog] = useState(false)
  const [selectedCode, setSelectedCode] = useState<PromoCode | null>(null)
  const [formData, setFormData] = useState({ code: '', description: '', expiresAt: '' })
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: queries.admin.promoCodes(),
    queryFn: () => api.getPromoCodes({ includeUsed: true, includeRevoked: true }),
  })

  const promoCodes = data?.promoCodes ?? []

  const createMutation = useMutation({
    mutationFn: (data: { code?: string; description?: string; expiresAt?: string }) =>
      api.createPromoCode(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queries.admin.promoCodes() })
      setShowCreateDialog(false)
      setFormData({ code: '', description: '', expiresAt: '' })
      setSuccessMessage(`Promo code "${result.promoCode.code}" created!`)
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.revokePromoCode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.promoCodes() })
      setShowRevokeDialog(false)
      setSelectedCode(null)
      setSuccessMessage('Promo code revoked.')
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  const handleCreate = () => {
    const createData: { code?: string; description?: string; expiresAt?: string } = {}
    if (formData.code?.trim()) createData.code = formData.code.trim()
    if (formData.description?.trim()) createData.description = formData.description.trim()
    if (formData.expiresAt) createData.expiresAt = new Date(formData.expiresAt).toISOString()
    createMutation.mutate(createData)
  }

  const handleCopy = async (code: string, id: string) => {
    try { await navigator.clipboard.writeText(code); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000) } catch { /* ignore */ }
  }

  const availableCount = promoCodes.filter((pc) => getPromoCodeStatus(pc) === 'available').length
  const usedCount = promoCodes.filter((pc) => getPromoCodeStatus(pc) === 'used').length
  const expiredCount = promoCodes.filter((pc) => getPromoCodeStatus(pc) === 'expired').length
  const revokedCount = promoCodes.filter((pc) => getPromoCodeStatus(pc) === 'revoked').length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Promo Codes</h1>
          <p className="text-gray-500">Create and manage one-time use promo codes for free access</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />Create Code
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{availableCount}</div><p className="text-sm text-gray-500">Available</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-gray-600">{usedCount}</div><p className="text-sm text-gray-500">Used</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-amber-600">{expiredCount}</div><p className="text-sm text-gray-500">Expired</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-red-600">{revokedCount}</div><p className="text-sm text-gray-500">Revoked</p></CardContent></Card>
      </div>

      {successMessage && <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4"><p className="text-sm text-emerald-700">{successMessage}</p></div>}

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">All Promo Codes</h2>
          <p className="text-sm text-gray-500">{promoCodes.length} code{promoCodes.length !== 1 ? 's' : ''} total</p>
        </div>

        {isLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No promo codes yet</p>
            <p className="text-sm">Create your first promo code to get started.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {promoCodes.map((promoCode) => {
                const status = getPromoCodeStatus(promoCode)
                return (
                  <TableRow key={promoCode.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-semibold">{promoCode.code}</code>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleCopy(promoCode.code, promoCode.id)}>
                          {copiedId === promoCode.id ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-gray-400" />}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell><StatusBadge status={status} /></TableCell>
                    <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{promoCode.description || '—'}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(promoCode.createdAt).toLocaleDateString()}</div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {promoCode.expiresAt ? <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(promoCode.expiresAt).toLocaleDateString()}</div> : '—'}
                    </TableCell>
                    <TableCell>
                      {status === 'available' && (
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => { setSelectedCode(promoCode); setShowRevokeDialog(true) }}>
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) setFormData({ code: '', description: '', expiresAt: '' }) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
            <DialogDescription>Generate a one-time use code that grants free monthly access</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Custom Code <span className="text-gray-400">(optional)</span></Label>
              <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="Leave blank to auto-generate" className="font-mono uppercase" />
              <p className="text-xs text-gray-500 mt-1">If left blank, a random code like XXXX-XXXX-XXXX will be generated.</p>
            </div>
            <div>
              <Label>Description <span className="text-gray-400">(optional)</span></Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="e.g., Beta tester reward" />
            </div>
            <div>
              <Label>Expiry Date <span className="text-gray-400">(optional)</span></Label>
              <Input type="date" value={formData.expiresAt} onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })} min={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="flex-1">
                {createMutation.isPending ? 'Creating...' : 'Create Code'}
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation */}
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Promo Code</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke <code className="font-mono font-semibold">{selectedCode?.code}</code>? This will permanently disable the code. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedCode(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => selectedCode && revokeMutation.mutate(selectedCode.id)}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke Code'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
